import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styles from '../header.scss';
import { t } from '../../../services/i18nService';
import backgroundAudioControlButtonSelector from '../../../selectors/header/buttons/BackgroundAudioControlButtonSelector';
import backgroundAudioService from '../../../services/BackgroundAudioService';
import backgroundAudioStatus from '../../../constants/BackgroundAudioStatus';
import apiService from '../../../services/api/ApiService';
import backgroundAudioInteractionType from '../../../constants/BackgroundAudioInteractionType';
import clientEventType from '../../../constants/ClientEventType';
import viewReadyTracker from '../../../services/ViewReadyTracker';
import { reportError } from '../../../services/ErrorReporting';
import sessionReader from '../../../store/readHelpers/SessionReader';
import settingsReader from '../../../store/readHelpers/SettingsReader';
import uiService from '../../../services/UIService';
import { UI_OPTIONS_LIST } from '../../../constants/UIConstants';
import storeManager from '../../../store/StoreManager';
import uiActions from '../../../actions/UIActions';

const BackgroundAudioControlButton = (props) => {
    const {
        assessment,
        audioEndTime,
        audioStartTime,
        audioStatus,
        buttonCaption,
        buttonIcon,
        isButtonDisabled,
        replaysStatusText,
        sectionBackgroundAudioPart,
        sectionNumber,
        timesReplayRequested,
    } = props;

    const [srExtraCaption, setSrExtraCaption] = useState('');

    const srDebounceTime = 100;
    let isSrExtraCaptionLocked = false;

    useEffect(() => {
        if (!settingsReader.shouldAllowPauseAndResumeOptionForTestTaker()) return;

        const previousContrastOption = uiService.getUISettingFromLocalStorageForTestTaker(UI_OPTIONS_LIST.CONTRAST_OPTION);
        const previousZoomFactor = uiService.getUISettingFromLocalStorageForTestTaker(UI_OPTIONS_LIST.ZOOM_FACTOR);

        const store = storeManager.getStore();

        if (previousContrastOption) {
            store.dispatch(uiActions.uiSetContrastOption(previousContrastOption));
        }

        if (previousZoomFactor) {
            store.dispatch(uiActions.uiSetZoomFactor(previousZoomFactor));
        }
    }, []);


    const addScreenReaderExtraCaption = () => {
        if (isSrExtraCaptionLocked) {
            return;
        }

        // set caption to the alert element. small timeout is needed to be sure it's announced after the button caption is read
        setTimeout(() => {
            setSrExtraCaption(replaysStatusText);
        }, srDebounceTime);
    };

    const clearScreenReaderExtraCaption = () => {
        // need to lock it for a short period of time because some screen readers trigger additional blur + focus events
        isSrExtraCaptionLocked = true;
        setSrExtraCaption('');
        setTimeout(() => {
            isSrExtraCaptionLocked = false;
        }, srDebounceTime);
    };

    const playAudio = () => {
        if (settingsReader.shouldAllowPauseAndResumeOptionForTestTaker()) {
            const cachedAudio = backgroundAudioService.getAudioPositionFromLocalCache();
            try {
                const audioData = cachedAudio ? JSON.parse(cachedAudio) : null;
                const { assessmentRunId, backgroundAudioPosition, userId } = audioData || {};

                const shouldResume = assessmentRunId === assessment?.assessmentRunId &&
                    userId === sessionReader.getUserId() && backgroundAudioPosition;

                backgroundAudioService.startAudioPlayFromOverlay(
                    shouldResume ? backgroundAudioPosition : null,
                    assessmentRunId || assessment?.assessmentRunId,
                );
            } catch (error) {
                backgroundAudioService.startAudioPlayFromOverlay();
                viewReadyTracker.logError();
                reportError(new Error(
                    `Failed to parse cached audio position in BackgroundAudioControlButton: ${error?.message ? error.message : error}`
                ));
            }
        } else {
            backgroundAudioService.playFromTo(
                assessment.assessmentRunId,
                sectionBackgroundAudioPart.contentItemId,
                audioStartTime,
                audioEndTime
            );
        }
    };

    const resumeAudio = () => {
        if (settingsReader.shouldAllowPauseAndResumeOptionForTestTaker()) {
            backgroundAudioService.resume();
        }
    };

    const pauseAudio = () => {
        if (settingsReader.shouldAllowPauseAndResumeOptionForTestTaker()) {
            backgroundAudioService.pause();
        } else {
            backgroundAudioService.pauseSectionAudio(sectionBackgroundAudioPart.contentItemId);
        }
    };

    const replayAudio = () => {
        backgroundAudioService.storeSectionAudioIncreasedTimesPlayed(sectionBackgroundAudioPart.contentItemId, timesReplayRequested + 1);
        playAudio();
    };

    const onButtonClick = () => {
        let interactionType;

        switch (audioStatus) {
        case backgroundAudioStatus.PLAYING:
            interactionType = backgroundAudioInteractionType.PAUSE;
            pauseAudio();
            break;
        case backgroundAudioStatus.PAUSED:
            interactionType = backgroundAudioInteractionType.RESUME;
            if (settingsReader.shouldAllowPauseAndResumeOptionForTestTaker()) {
                resumeAudio();
            } else {
                playAudio();
            }
            break;
        case backgroundAudioStatus.ENDED:
            interactionType = backgroundAudioInteractionType.REPLAY;
            replayAudio();
            break;
        default:
            // INIT, REWOUND
            interactionType = backgroundAudioInteractionType.START;
            playAudio();
            break;
        }

        const extraInfo = {
            audioInteractionType: interactionType,
            sectionNumber,
        };
        apiService.notifyClientEvent(clientEventType.SECTION_AUDIO_INTERACTED, extraInfo, true);
    };

    return (<div className={styles.audioControlWrapper}>
        <div>
            <button
                className={`${styles.audioControlButton} btn`}
                onClick={isButtonDisabled ? null : onButtonClick}
                id="background-audio-control-button"
                disabled={isButtonDisabled}
                aria-labelledby="replays-hint"
                onFocus={addScreenReaderExtraCaption}
                onBlur={clearScreenReaderExtraCaption}
            >
                <span className="sr-only">{t('header.backgroundAudioControl')}</span>
                <i className={`fa ${buttonIcon}`} aria-hidden="true" />
                <span className={styles.audioControlCaption}>
                    { buttonCaption }
                </span>
            </button>

            <span role="alert" aria-live="polite" className="sr-only">{ srExtraCaption }</span>
        </div>

        { replaysStatusText &&
            <div className={styles.replaysStatusText}>
                { replaysStatusText }
            </div>
        }
    </div>);
};

BackgroundAudioControlButton.propTypes = {
    assessment: PropTypes.object.isRequired,
    audioEndTime: PropTypes.number.isRequired,
    audioStartTime: PropTypes.number,
    audioStatus: PropTypes.string.isRequired,
    buttonCaption: PropTypes.string.isRequired,
    buttonIcon: PropTypes.string.isRequired,
    isButtonDisabled: PropTypes.bool.isRequired,
    replaysStatusText: PropTypes.string.isRequired,
    sectionBackgroundAudioPart: PropTypes.object.isRequired,
    sectionNumber: PropTypes.number.isRequired,
    timesReplayRequested: PropTypes.number.isRequired,
};

const mapStateToProps = state => backgroundAudioControlButtonSelector.selectProps(state);
export default connect(mapStateToProps)(BackgroundAudioControlButton);
